import React, {
    useEffect,
    useRef,
    useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import MobileFrame from "../../components/MobileFrame";
import { Loader2, Home } from "lucide-react";

import { submitGatewayAddress } from "../../api/gateway.api";
import { fetchPincodeDetails } from "../../api/pincode.api";
import { ApiError } from "../../api/ApiError";

import { setJourney } from "../../store/journeySlice";

import type {
    RootState,
    AppDispatch,
} from "../../store";

/* =========================================================
 * TYPES
 * ========================================================= */

type OwnershipType =
    | "Owned"
    | "Rented"
    | "FamilyOwned";

type Step =
    | "form"
    | "submitting";

type JourneyPhase =
    | "ADDRESS"
    | "PRE_APPROVAL"
    | "SCHEME_SELECTION"
    | "NATIVE"
    | "COMPLETED"
    | "REJECTED";

interface AddressFields {
    companyName: string;
    city: string;
    state: string;
    fullAddress: string;
    area: string;
    ownershipType: OwnershipType | "";
}

interface AddressErrors {
    companyName?: string;
    city?: string;
    state?: string;
    fullAddress?: string;
    area?: string;
    ownershipType?: string;
}

const OWNERSHIP_OPTIONS: {
    value: OwnershipType;
    label: string;
}[] = [
        {
            value: "Owned",
            label: "Owned",
        },
        {
            value: "Rented",
            label: "Rented",
        },
        {
            value: "FamilyOwned",
            label: "Family",
        },
    ];

const EMPTY_ADDRESS: AddressFields = {
    companyName: "",
    city: "",
    state: "",
    fullAddress: "",
    area: "",
    ownershipType: "",
};

const INPUT_CLASS = `
    mt-0.5
    w-full
    rounded-lg
    border
    border-gray-200
    bg-transparent
    px-3
    py-2
    text-[13px]
    font-semibold
    text-gray-900
    outline-none
    transition-all
    appearance-none
    placeholder:font-normal
    placeholder:text-gray-400
    focus:border-indigo-500
    focus:ring-2
    focus:ring-indigo-100
    disabled:cursor-not-allowed
    disabled:bg-transparent
    disabled:opacity-60
    disabled:font-semibold
    disabled:text-gray-900
    [-webkit-text-fill-color:theme(colors.gray.900)]
    [&:-webkit-autofill]:[-webkit-box-shadow:0_0_0px_1000px_transparent_inset]
    [&:-webkit-autofill]:[-webkit-text-fill-color:theme(colors.gray.900)]
`;

/* =========================================================
 * COMPONENT
 * ========================================================= */

const AddressForm: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const journey = useSelector(
        (state: RootState) =>
            state.journey.journey
    );

    const prefilledCity = useSelector(
        (state: RootState) =>
            state.journey.prefilledCity
    );

    const prefilledState = useSelector(
        (state: RootState) =>
            state.journey.prefilledState
    );

    const [address, setAddress] =
        useState<AddressFields>(() => ({
            ...EMPTY_ADDRESS,
            city: prefilledCity ?? "",
            state: prefilledState ?? "",
        }));

    const [isCityStateLoading, setIsCityStateLoading] =
        useState(false);

    const [cityStateFetchFailed, setCityStateFetchFailed] =
        useState(false);

    /*
     * =========================================================
     * FALLBACK CITY/STATE FETCH
     * =========================================================
     *
     * Normally city/state arrive pre-fetched from the OTP
     * page via Redux. If Redux was reset (refresh, direct
     * navigation, session restore) and they're missing here,
     * fetch them as a fallback so the user isn't stuck with
     * empty, disabled fields.
     */
    useEffect(() => {

        if (
            prefilledCity ||
            prefilledState ||
            !journey?.customerPincode
        ) {
            return;
        }

        let cancelled = false;

        (async () => {

            setIsCityStateLoading(true);
            setCityStateFetchFailed(false);

            try {

                const result =
                    await fetchPincodeDetails(
                        journey.customerPincode
                    );

                if (cancelled) {
                    return;
                }

                if (result) {

                    setAddress(
                        (previous) => ({
                            ...previous,
                            city: result.city,
                            state: result.state,
                        })
                    );

                } else {

                    setCityStateFetchFailed(
                        true
                    );

                }

            } catch {
                if (!cancelled) {
                    setCityStateFetchFailed(true);
                }
            } finally {

                if (!cancelled) {
                    setIsCityStateLoading(
                        false
                    );
                }

            }

        })();

        return () => {
            cancelled = true;
        };

    }, [
        prefilledCity,
        prefilledState,
        journey?.customerPincode,
    ]);

    const [addressErrors, setAddressErrors] =
        useState<AddressErrors>({});

    const [step, setStep] =
        useState<Step>("form");

    const [submitError, setSubmitError] =
        useState<string>("");


    /*
 * Ref for Full Address field.
 *
 * City/state are prefilled (fetched earlier on
 * the OTP page) and disabled here, so Full Address
 * is the first field the user actually fills in.
 */
    const fullAddressRef =
        useRef<HTMLTextAreaElement>(null);

    /*
     * =========================================================
     * AUTO FOCUS FULL ADDRESS FIELD
     * =========================================================
     */
    useEffect(() => {

        const frame =
            requestAnimationFrame(() => {

                fullAddressRef
                    .current
                    ?.focus();

            });

        return () => {

            cancelAnimationFrame(
                frame
            );

        };

    }, []);

    const isSubmitting =
        step === "submitting";



    /* =========================================================
     * FIELD CHANGE
     * ========================================================= */

    const handleAddressChange = (
        field: keyof AddressFields,
        value: string
    ) => {
        setAddress(
            (previous) => ({
                ...previous,
                [field]: value,
            })
        );

        setAddressErrors(
            (previous) => ({
                ...previous,
                [field]: undefined,
            })
        );

        setSubmitError("");
    };

    /* =========================================================
     * VALIDATION
     * ========================================================= */

    const validateFields = (
        fields: AddressFields
    ): AddressErrors => {
        const errors: AddressErrors = {};

        if (!fields.companyName.trim()) {
            errors.companyName =
                "Company name is required.";
        }

        if (!fields.city.trim()) {
            errors.city =
                "City is required.";
        }

        if (!fields.state.trim()) {
            errors.state =
                "State is required.";
        }

        if (!fields.fullAddress.trim()) {
            errors.fullAddress =
                "Full address is required.";
        }

        if (!fields.area.trim()) {
            errors.area =
                "Area is required.";
        }

        if (!fields.ownershipType) {
            errors.ownershipType =
                "Select ownership type.";
        }

        return errors;
    };

    const validate = (): boolean => {
        const errors =
            validateFields(address);

        setAddressErrors(errors);

        return (
            Object.keys(errors).length === 0
        );
    };

    /* =========================================================
     * NAVIGATION
     * ========================================================= */

    const navigateByJourneyPhase = (
        phase: JourneyPhase
    ) => {
        switch (phase) {
            case "PRE_APPROVAL":
                navigate("/napproval", {
                    replace: true,
                });
                return;

            case "SCHEME_SELECTION":
                navigate(
                    "/nscheme-selection",
                    {
                        replace: true,
                    }
                );
                return;

            case "REJECTED":
                navigate("/nrejected", {
                    replace: true,
                });
                return;

            case "NATIVE":
            case "COMPLETED":
                navigate("/ndashboard", {
                    replace: true,
                });
                return;

            case "ADDRESS":
                return;

            default:
                setSubmitError(
                    "Unable to determine the next step. Please try again."
                );
        }
    };

    /* =========================================================
     * SUBMIT
     * ========================================================= */

    const handleContinue = async () => {
        if (isSubmitting) {
            return;
        }

        if (!journey) {
            setSubmitError(
                "Your application session is unavailable. Please open the application link again."
            );

            return;
        }

        if (!validate()) {
            return;
        }

        try {
            setStep("submitting");
            setSubmitError("");

            const response =
                await submitGatewayAddress({
                    companyName:
                        address.companyName.trim(),
                    address: {
                        city:
                            address.city.trim(),
                        state:
                            address.state.trim(),
                        fullAddress:
                            address.fullAddress.trim(),
                        area:
                            address.area.trim(),
                        ownershipType:
                            address.ownershipType as OwnershipType,
                    },
                });

            if (
                !response?.data?.journey
            ) {
                throw new Error(
                    "Invalid response received while saving your address."
                );
            }

            const updatedJourney =
                response.data.journey;

            dispatch(
                setJourney(
                    updatedJourney
                )
            );

            navigateByJourneyPhase(
                updatedJourney.phase as JourneyPhase
            );

        } catch (error) {

            let message =
                "Unable to save your address. Please try again.";

            if (error instanceof ApiError) {

                if (
                    error.status >= 500 &&
                    error.status < 600
                ) {
                    message =
                        "Something went wrong. Please try again later.";
                } else {
                    message =
                        error.message?.trim() || message;
                }
            }

            setSubmitError(message);

            setStep("form");
        }
    };

    /* =========================================================
     * SECTION HEADING
     * ========================================================= */

    const renderSectionHeading = (
        label: string
    ) => (
        <div className="flex items-center gap-2">

            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                {label}
            </p>

            <div
                aria-hidden="true"
                className="h-px flex-1 bg-gray-100"
            />

        </div>
    );

    const isAddressComplete =
        address.companyName.trim() !== "" &&
        address.fullAddress.trim() !== "" &&
        address.area.trim() !== "" &&
        address.ownershipType !== "" &&
        address.city.trim() !== "" &&
        address.state.trim() !== "";

    /* =========================================================
     * ADDRESS FIELDS
     * ========================================================= */

    const renderAddressFields = () => {



        return (
            <div className="space-y-2">

                {/* FULL ADDRESS */}

                <div>

                    <label
                        htmlFor="full-address"
                        className="text-[11px] font-medium text-gray-700"
                    >
                        Full address
                    </label>

                    <textarea
                        ref={fullAddressRef}
                        id="full-address"
                        value={
                            address.fullAddress
                        }
                        disabled={
                            isSubmitting
                        }
                        autoComplete="street-address"
                        onChange={(e) =>
                            handleAddressChange(
                                "fullAddress",
                                e.target.value
                            )
                        }
                        rows={2}
                        aria-invalid={
                            !!addressErrors.fullAddress
                        }
                        className={`${INPUT_CLASS} resize-none`}
                    />

                    {addressErrors.fullAddress && (
                        <p className="mt-0.5 text-[10.5px] font-medium text-red-500">
                            {
                                addressErrors.fullAddress
                            }
                        </p>
                    )}

                </div>

                {/* AREA */}

                <div>

                    <label
                        htmlFor="area"
                        className="text-[11px] font-medium text-gray-700"
                    >
                        Area/Landmark
                    </label>

                    <input
                        id="area"
                        type="text"
                        value={address.area}
                        disabled={
                            isSubmitting
                        }
                        onChange={(e) =>
                            handleAddressChange(
                                "area",
                                e.target.value
                            )
                        }
                        aria-invalid={
                            !!addressErrors.area
                        }
                        className={
                            INPUT_CLASS
                        }
                    />

                    {addressErrors.area && (
                        <p className="mt-0.5 text-[10.5px] font-medium text-red-500">
                            {
                                addressErrors.area
                            }
                        </p>
                    )}

                </div>

                {/* OWNERSHIP */}

                <div>

                    <label className="text-[11px] font-medium text-gray-700">
                        Ownership type
                    </label>

                    <div
                        className="mt-1 grid grid-cols-3 gap-1.5"
                        role="group"
                        aria-label="Ownership type"
                    >

                        {OWNERSHIP_OPTIONS.map(
                            (option) => {

                                const isSelected =
                                    address.ownershipType ===
                                    option.value;

                                return (
                                    <button
                                        key={
                                            option.value
                                        }
                                        type="button"
                                        disabled={
                                            isSubmitting
                                        }
                                        aria-pressed={
                                            isSelected
                                        }
                                        onClick={() =>
                                            handleAddressChange(
                                                "ownershipType",
                                                option.value
                                            )
                                        }
                                        className={`
                            flex
                            items-center
                            justify-center
                            gap-1
                            rounded-lg
                            border
                            py-1.5
                            text-[11px]
                            font-medium
                            transition-all
                            disabled:cursor-not-allowed
                            disabled:opacity-60
                            ${isSelected
                                                ? "border-indigo-500 bg-indigo-50 text-indigo-600"
                                                : "border-gray-200 bg-white text-gray-600 hover:border-indigo-300"
                                            }
                        `}
                                    >

                                        <Home
                                            aria-hidden="true"
                                            className="h-3 w-3"
                                        />

                                        {
                                            option.label
                                        }

                                    </button>
                                );
                            }
                        )}

                    </div>

                    {addressErrors.ownershipType && (
                        <p className="mt-0.5 text-[10.5px] font-medium text-red-500">
                            {
                                addressErrors.ownershipType
                            }
                        </p>
                    )}

                </div>

                {/* CITY + STATE */}

                <div className="grid grid-cols-2 gap-2.5">

                    <div>

                        <label
                            htmlFor="city"
                            className="text-[11px] font-medium text-gray-700"
                        >
                            City
                        </label>

                        <div className="relative">

                            <input
                                id="city"
                                type="text"
                                value={address.city}
                                disabled
                                autoComplete="address-level2"
                                placeholder="City"
                                className={INPUT_CLASS}
                            />

                        </div>

                        {addressErrors.city && (
                            <p className="mt-0.5 text-[10.5px] font-medium text-red-500">
                                {
                                    addressErrors.city
                                }
                            </p>
                        )}

                    </div>

                    <div>

                        <label
                            htmlFor="state"
                            className="text-[11px] font-medium text-gray-700"
                        >
                            State
                        </label>

                        <div className="relative">

                            <input
                                id="state"
                                type="text"
                                value={address.state}
                                disabled
                                autoComplete="address-level1"
                                placeholder="State"
                                className={INPUT_CLASS}
                            />

                        </div>

                        {addressErrors.state && (
                            <p className="mt-0.5 text-[10.5px] font-medium text-red-500">
                                {
                                    addressErrors.state
                                }
                            </p>
                        )}

                    </div>

                </div>

            </div>
        );
    };

    /* =========================================================
     * COMPANY FIELD (separate section, not part of address)
     * ========================================================= */

    const renderCompanyField = () => (
        <div>

            <label
                htmlFor="company-name"
                className="text-[11px] font-medium text-gray-700"
            >
                Company name
            </label>

            <input
                id="company-name"
                type="text"
                value={address.companyName}
                disabled={
                    isSubmitting
                }
                maxLength={100}
                autoComplete="organization"
                onChange={(e) =>
                    handleAddressChange(
                        "companyName",
                        e.target.value
                    )
                }
                aria-invalid={
                    !!addressErrors.companyName
                }
                className={
                    INPUT_CLASS
                }
            />

            {addressErrors.companyName && (
                <p className="mt-0.5 text-[10.5px] font-medium text-red-500">
                    {
                        addressErrors.companyName
                    }
                </p>
            )}

        </div>
    );

    /* =========================================================
     * UI
     * ========================================================= */

    return (
        <MobileFrame
            glowColor="#c7d2fe"
            borderColor="#e5e7eb"
            width={420}
        >

            <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100">

                <div
                    aria-hidden="true"
                    className="absolute -left-20 -top-24 h-64 w-64 rounded-full bg-purple-200 opacity-20 blur-3xl"
                />

                <div
                    aria-hidden="true"
                    className="absolute -bottom-24 -right-20 h-64 w-64 rounded-full bg-blue-200 opacity-20 blur-3xl"
                />

                <div className="relative z-10 flex h-full flex-col overflow-y-auto px-5 pb-3 pt-4">

                    {/* HEADER */}

                    <div className="flex items-center justify-between">

                        <p className="text-base font-bold text-gray-900">
                            Add Address Details
                        </p>

                        <img
                            src="/CleverPeLogos/CleverPE_Black_icon.svg"
                            alt="CleverPe"
                            className="h-6 w-6 opacity-90"
                        />

                    </div>

                    <p className="mt-1 text-[12px] leading-snug text-gray-500">
                        Share your address details to
                        proceed with your application
                    </p>

                    <div className="mt-3">
                        {renderSectionHeading(
                            "Address details"
                        )}
                    </div>

                    <div className="mt-2">
                        {renderAddressFields()}
                    </div>

                    <div className="mt-10">
                        {renderSectionHeading(
                            "Company details"
                        )}
                    </div>

                    <div className="mt-2">
                        {renderCompanyField()}
                    </div>

                    {submitError && (
                        <p
                            role="alert"
                            className="mt-2 text-center text-[12px] font-medium text-red-500"
                        >
                            {submitError}
                        </p>
                    )}

                    <div className="flex-1" />

                    <button
                        type="button"
                        onClick={handleContinue}
                        disabled={isSubmitting || !isAddressComplete}
                        className={`
        mt-3
        mb-3
        flex
        w-full
        items-center
        justify-center
        gap-2
        rounded-full
        py-2.5
        text-sm
        font-semibold
        transition-all
        ${isSubmitting || !isAddressComplete
                                ? "cursor-not-allowed bg-gray-200 text-gray-400"
                                : "bg-gradient-to-r from-indigo-500 to-blue-400 text-white hover:opacity-95"
                            }
    `}
                    >
                        {isSubmitting && (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        )}

                        {isSubmitting
                            ? "Saving..."
                            : "Continue"}
                    </button>

                    <p className="mb-2 text-center text-[11px] font-semibold tracking-wide text-gray-400">
                        CleverPe
                    </p>

                </div>

            </div>

        </MobileFrame>
    );
};

export default AddressForm;